"use client";

import { motion, useReducedMotion } from "motion/react";
import Image from "next/image";
import Link from "next/link";

import { REVEAL_STAGGER, TextReveal } from "@/components/elements/text-reveal";
import { type Crumb } from "@/components/sections/breadcrumbs";
import { JsonLd } from "@/components/seo/json-ld";
import { Button } from "@/components/ui/button";
import { appStoreUrl } from "@/lib/constants";
import { breadcrumbLd } from "@/lib/seo";
import { cn } from "@/lib/utils";

const FLOATING_CARDS = [
  {
    label: "💍 The big day",
    src: "/images/hero/cards/big-day.jpg",
    position: "left-[3%] top-[12%]",
    rotate: -6,
    width: "w-[195px]",
    entrance: { delay: 0.5 },
  },
  {
    label: "🌅 Golden hour",
    src: "/images/hero/cards/golden-hour.jpg",
    position: "right-[4%] top-[8%]",
    rotate: 5,
    width: "w-[172px]",
    entrance: { delay: 0.65 },
  },
  {
    label: "🌙 After dark",
    src: "/images/hero/cards/after-dark.jpg",
    position: "right-[2%] top-[52%]",
    rotate: -4,
    width: "w-[156px]",
    entrance: { delay: 0.8 },
  },
];

interface UseCaseHeroProps {
  label: string;
  title: string;
  subtitle: string;
  description: string;
  /** Kept for call-site compatibility; the redesigned hero uses floating photo cards. */
  image?: string;
  /** Handwritten margin note, e.g. "the ones you didn't see ". */
  annotation?: string;
  /** Per-use-case floating cards (label + src). Layout/positioning stays fixed; falls back to defaults per slot. */
  cards?: { label: string; src: string }[];
  /** Breadcrumb trail (Home → …). Renders the visible nav + matching BreadcrumbList JSON-LD. */
  breadcrumbs?: readonly Crumb[];
  /** App Store campaign token for the hero CTA. */
  ctaCampaign?: string;
  /** Hero CTA button label. */
  ctaLabel?: string;
}

export function UseCaseHero({
  label,
  title,
  subtitle,
  description,
  annotation,
  cards,
  breadcrumbs,
  ctaCampaign = "web_usecase_hero",
  ctaLabel = "Create your album",
}: UseCaseHeroProps) {
  const reduced = useReducedMotion();

  return (
    <section className="hero-padding relative overflow-hidden">
      {/* Floating photo cards — layer starts below the fixed nav */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-[88px] bottom-0 hidden lg:block"
      >
        {FLOATING_CARDS.map((card, i) => {
          const src = cards?.[i]?.src ?? card.src;
          const label = cards?.[i]?.label ?? card.label;
          return (
          <motion.div
            key={card.position}
            className={cn("absolute", card.position)}
            style={{ rotate: card.rotate }}
            initial={
              reduced ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.92 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.7,
              delay: card.entrance.delay,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <div
              className={cn(
                "relative drop-shadow-[0px_8px_9px_rgba(0,0,0,0.14)]",
                card.width,
              )}
            >
              <Image
                src={src}
                alt=""
                width={390}
                height={296}
                className="aspect-[195/148] w-full rounded-3xl object-cover"
              />
              <span className="bg-secondary text-secondary-foreground absolute -bottom-3.5 left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1 text-xs whitespace-nowrap">
                {label}
              </span>
            </div>
          </motion.div>
          );
        })}
      </div>

      <div className="relative z-10 container">
        {/* Breadcrumb nav is intentionally not rendered visually — the
            BreadcrumbList structured data is still emitted for SEO/rich results. */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <JsonLd data={breadcrumbLd(breadcrumbs)} />
        )}
        <motion.div
          className="mx-auto flex max-w-3xl flex-col items-center text-center"
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: {} }}
        >
          <TextReveal mode="animate">
            <span className="text-text-tertiary font-mono text-xs font-medium tracking-[3px] uppercase">
              {label}
            </span>
          </TextReveal>
          <TextReveal mode="animate" delay={REVEAL_STAGGER} className="mt-5">
            <h1 className="text-4xl tracking-tight text-balance md:text-5xl lg:text-[56px] lg:leading-[66px]">
              {title}
            </h1>
          </TextReveal>
          <TextReveal
            mode="animate"
            delay={REVEAL_STAGGER * 2}
            className="mt-8"
          >
            <p className="text-foreground text-lg font-medium">{subtitle}</p>
          </TextReveal>
          <TextReveal
            mode="animate"
            delay={REVEAL_STAGGER * 3}
            className="mt-2"
          >
            <p className="text-muted-foreground text-base">{description}</p>
          </TextReveal>

          <motion.div
            className="mt-10"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.4 }}
          >
            <Button asChild className="h-[52px] rounded-xl px-11 text-[15px]">
              <Link
                href={appStoreUrl(ctaCampaign)}
                target="_blank"
                rel="noopener"
              >
                {ctaLabel}
              </Link>
            </Button>
          </motion.div>
        </motion.div>

        {annotation && (
          <motion.p
            aria-hidden
            className="font-handwritten text-text-secondary absolute right-[10%] -bottom-4 hidden -rotate-8 text-[26px] whitespace-nowrap xl:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            {annotation}
          </motion.p>
        )}
      </div>
    </section>
  );
}
